import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import jwtDecode from "jwt-decode";

const AuthContext = createContext();

export default AuthContext;

const BASE_URL = (process.env.REACT_APP_API_URL || "") + "/api/auth";

function buildUserObject(decoded) {
  if (!decoded) return null;
  return {
    id: decoded.user_id,
    username: decoded.username,
    first_name: decoded.first_name,
    role: decoded.role || 'user',
    show_submission_prompt: decoded.show_submission_prompt ?? true,
    has_passkey: decoded.has_passkey ?? false,
    passkey_setup_dismissed: decoded.passkey_setup_dismissed ?? false,
  };
}

// ArrayBuffer ↔ base64url helpers for WebAuthn
function bufToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function b64urlToBuf(b64url) {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const buf = new ArrayBuffer(bin.length);
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return buf;
}

export const AuthProvider = ({ children }) => {
  const storedToken = JSON.parse(localStorage.getItem("token"));
  const decoded = storedToken ? jwtDecode(storedToken) : null;
  const isTokenExpired = decoded ? (decoded.exp * 1000 < Date.now()) : false;

  const [token, setToken] = useState(isTokenExpired ? null : storedToken);
  const [user, setUser] = useState(isTokenExpired ? null : buildUserObject(decoded));
  const [isServerError, setIsServerError] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [totpPending, setTotpPending] = useState(null);
  // After password login with no passkey: prompt to set one up before redirecting
  const [passkeySetupPending, setPasskeySetupPending] = useState(null); // { access, refresh }
  // false while we're attempting a silent token refresh on startup; PrivateRoute waits for true
  const [authReady, setAuthReady] = useState(!isTokenExpired);
  const navigate = useNavigate();

  // Fire-and-forget wake-up ping the moment the app loads. Railway sleeps this
  // service when it's been idle, and the first real request pays for the wake-up.
  // Firing this immediately on load means that cost lands while someone's still
  // typing their username/password instead of after they hit Login.
  useEffect(() => {
    axios.get(`${BASE_URL}/ping/`).catch(() => {});
  }, []);

  const _forceLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    setUser(null);
    setToken(null);
    setTotpPending(null);
    setPasskeySetupPending(null);
    navigate("/login");
  };

  // On startup: if the stored access token is expired, try a silent refresh before showing the app.
  // This prevents users from seeing a broken "logged-in but can't load data" state after a long absence.
  useEffect(() => {
    if (!isTokenExpired) return;
    const storedRefresh = JSON.parse(localStorage.getItem("refreshToken"));
    if (!storedRefresh) {
      localStorage.removeItem("token");
      setAuthReady(true);
      return;
    }
    axios.post(`${BASE_URL}/login/refresh/`, { refresh: storedRefresh })
      .then(res => {
        const newAccess = res.data.access;
        const newRefresh = res.data.refresh || storedRefresh;
        localStorage.setItem("token", JSON.stringify(newAccess));
        localStorage.setItem("refreshToken", JSON.stringify(newRefresh));
        const newDecoded = jwtDecode(newAccess);
        setToken(newAccess);
        setUser(buildUserObject(newDecoded));
      })
      .catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
      })
      .finally(() => setAuthReady(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Intercept 401s globally: refresh silently, retry, logout only on refresh failure.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        if (error.response?.status === 401 && !original._retry && !original.url?.includes('/login/refresh/')) {
          original._retry = true;
          const storedRefresh = JSON.parse(localStorage.getItem("refreshToken"));
          if (storedRefresh) {
            try {
              const res = await axios.post(`${BASE_URL}/login/refresh/`, { refresh: storedRefresh });
              const newAccess = res.data.access;
              localStorage.setItem("token", JSON.stringify(newAccess));
              setToken(newAccess);
              original.headers['Authorization'] = `Bearer ${newAccess}`;
              return axios(original);
            } catch {
              _forceLogout();
            }
          } else {
            _forceLogout();
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const _finalizeLogin = (accessToken, refreshToken) => {
    localStorage.setItem("token", JSON.stringify(accessToken));
    if (refreshToken) localStorage.setItem("refreshToken", JSON.stringify(refreshToken));
    setToken(accessToken);
    // Bug fix: this used to decode the stale `decoded` closed over from whatever
    // token was in localStorage when AuthProvider last rendered (null on a fresh
    // login) instead of the token this call just received — crashed on
    // `userObj.role` below on every first-ever login. Decode the token we were
    // actually just given.
    const userObj = buildUserObject(jwtDecode(accessToken));
    setUser(userObj);
    setIsServerError(false);
    setTotpPending(null);
    setPasskeySetupPending(null);
    navigate(userObj.role === 'admin' ? '/admin' : '/submit');
  };

  // Backoff schedule for silent retries on a connectionless login failure.
  // Two retries (not just one) because we've observed a single 2s retry isn't
  // always enough to survive whatever's dropping the request — see CLAUDE.md.
  const LOGIN_RETRY_DELAYS_MS = [2000, 4000];

  const loginUser = async (loginData, _retryCount = 0) => {
    if (_retryCount === 0) {
      setLoginErrorMessage("");
      setLoginLoading(true);
    }
    try {
      const response = await axios.post(`${BASE_URL}/login/`, loginData);
      if (response.status === 200) {
        if (response.data.requires_totp) {
          setTotpPending({ totp_token: response.data.totp_token });
          setIsServerError(false);
          setLoginLoading(false);
        } else {
          const { access, refresh } = response.data;
          // Show the passkey setup prompt only on biometric-capable devices, for users
          // who don't already have a passkey, and haven't dismissed the prompt before.
          const loginDecoded = jwtDecode(access);
          const eligibleForPrompt =
            !!window.PublicKeyCredential &&
            !loginDecoded.has_passkey &&
            !loginDecoded.passkey_setup_dismissed;
          if (eligibleForPrompt) {
            setPasskeySetupPending({ access, refresh });
            setIsServerError(false);
            setLoginLoading(false);
          } else {
            _finalizeLogin(access, refresh);
            setLoginLoading(false);
          }
        }
      }
    } catch (error) {
      // No `error.response` means the request never got a reply at all — a dropped
      // connection, or the backend still spinning up after being idle. That is NOT
      // a bad password. Retry silently, with backoff, before bothering the user.
      const isNetworkError = !error.response;
      if (isNetworkError && _retryCount < LOGIN_RETRY_DELAYS_MS.length) {
        await new Promise(resolve => setTimeout(resolve, LOGIN_RETRY_DELAYS_MS[_retryCount]));
        return loginUser(loginData, _retryCount + 1);
      }
      console.error("Login error:", error.response?.data || error.message);
      setIsServerError(true);
      setLoginLoading(false);
      if (error.response?.status === 401) {
        setLoginErrorMessage("Incorrect username or password.");
      } else if (isNetworkError) {
        setLoginErrorMessage("Couldn't reach the server. It may still be starting up — please try again in a few seconds.");
      } else {
        setLoginErrorMessage("Something went wrong. Please try again.");
      }
    }
  };

  const verifyTotp = async (code) => {
    if (!totpPending) return;
    try {
      const response = await axios.post(`${BASE_URL}/totp/verify/`, {
        totp_token: totpPending.totp_token,
        code,
      });
      _finalizeLogin(response.data.access, response.data.refresh);
    } catch (error) {
      setIsServerError(true);
    }
  };

  const skipPasskeySetup = () => {
    if (!passkeySetupPending) return;
    const { access, refresh } = passkeySetupPending;
    // Persist the dismissal so we stop asking on future logins. Best-effort — if this
    // fails (e.g. offline), still let the user into the app rather than blocking on it.
    axios.patch(
      `${BASE_URL}/preferences/`,
      { passkey_setup_dismissed: true },
      { headers: { Authorization: `Bearer ${access}` } },
    ).catch(err => console.error('Failed to save passkey prompt dismissal:', err));
    _finalizeLogin(access, refresh);
  };

  const registerPasskey = async (deviceName) => {
    if (!passkeySetupPending && !token) return { error: 'Not authenticated' };
    const authToken = passkeySetupPending?.access || token;
    const authHeader = { Authorization: `Bearer ${authToken}` };
    try {
      // 1. Get registration options from backend
      const beginRes = await axios.post(`${BASE_URL}/passkey/register/begin/`, {}, { headers: authHeader });
      const { options, challenge_token } = beginRes.data;

      // 2. Decode options for the browser API
      const publicKey = {
        ...options,
        challenge: b64urlToBuf(options.challenge),
        user: { ...options.user, id: b64urlToBuf(options.user.id) },
        excludeCredentials: (options.excludeCredentials || []).map(c => ({
          ...c, id: b64urlToBuf(c.id),
        })),
      };

      // 3. Prompt Face ID / Touch ID
      const credential = await navigator.credentials.create({ publicKey });

      // 4. Serialize response for backend
      const serialized = {
        id: credential.id,
        rawId: bufToB64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufToB64url(credential.response.clientDataJSON),
          attestationObject: bufToB64url(credential.response.attestationObject),
        },
      };

      // 5. Complete registration
      await axios.post(
        `${BASE_URL}/passkey/register/complete/`,
        { credential: serialized, challenge_token, device_name: deviceName || '' },
        { headers: authHeader },
      );

      // 6. Finish login if this was post-login setup
      if (passkeySetupPending) {
        _finalizeLogin(passkeySetupPending.access, passkeySetupPending.refresh);
      }
      return { success: true };
    } catch (err) {
      if (err.name === 'NotAllowedError') return { error: 'Cancelled' };
      return { error: err.response?.data?.error || err.message || 'Setup failed' };
    }
  };

  const loginWithPasskey = async (username) => {
    try {
      // 1. Get authentication options
      const beginRes = await axios.post(`${BASE_URL}/passkey/login/begin/`, { username });
      const { options, challenge_token } = beginRes.data;

      if (!options.allowCredentials?.length) {
        return { error: 'No passkey found for this account.' };
      }

      // 2. Decode for browser
      const publicKey = {
        ...options,
        challenge: b64urlToBuf(options.challenge),
        allowCredentials: (options.allowCredentials || []).map(c => ({
          ...c, id: b64urlToBuf(c.id),
        })),
      };

      // 3. Prompt biometric
      const credential = await navigator.credentials.get({ publicKey });

      // 4. Serialize
      const serialized = {
        id: credential.id,
        rawId: bufToB64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufToB64url(credential.response.clientDataJSON),
          authenticatorData: bufToB64url(credential.response.authenticatorData),
          signature: bufToB64url(credential.response.signature),
          userHandle: credential.response.userHandle ? bufToB64url(credential.response.userHandle) : null,
        },
      };

      // 5. Complete — returns JWT directly (TOTP bypassed)
      const completeRes = await axios.post(`${BASE_URL}/passkey/login/complete/`, {
        credential: serialized,
        challenge_token,
        username,
      });
      _finalizeLogin(completeRes.data.access, completeRes.data.refresh);
      return { success: true };
    } catch (err) {
      if (err.name === 'NotAllowedError') return { error: 'Cancelled' };
      return { error: err.response?.data?.error || 'Passkey sign-in failed.' };
    }
  };

  const updatePreference = async (field, value) => {
    try {
      await axios.patch(
        `${BASE_URL}/preferences/`,
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUser(prev => prev ? { ...prev, [field]: value } : prev);
    } catch (err) {
      console.error('Failed to update preference:', err);
    }
  };

  const logoutUser = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    setUser(null);
    setToken(null);
    setTotpPending(null);
    setPasskeySetupPending(null);
    navigate("/login");
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` },
  });

  return (
    <AuthContext.Provider value={{
      user, token, loginUser, logoutUser,
      verifyTotp, totpPending,
      passkeySetupPending, skipPasskeySetup, registerPasskey, loginWithPasskey,
      isServerError, loginErrorMessage, loginLoading, getAuthHeader, updatePreference, authReady,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
